import { Dispatch, SetStateAction, useEffect } from 'react';
import { useRerender } from 'src/shared/use-rerender';

export type InvalidateType = 'extensionTaxonomyGuid';

type WindowWithCaches = typeof window & { singletonCaches?: Record<string, [unknown, InvalidateType | undefined, string | undefined]> };

export function useSingletonState<T>(name: string, initialValue: T, invalidateType?: InvalidateType): [T, Dispatch<SetStateAction<T>>] {
  const win = window as WindowWithCaches;
  win.singletonCaches ||= {};
  let invalidateWhenChanges: string | undefined;
  if (invalidateType === 'extensionTaxonomyGuid') {
    invalidateWhenChanges = window.__extensionTaxonomyGuid;
  }
  win.singletonCaches[name] ??= [initialValue, invalidateType, invalidateWhenChanges];
  win.singletonCaches[name][2] ??= invalidateWhenChanges;
  if (win.singletonCaches[name][2] !== invalidateWhenChanges) {
    win.singletonCaches[name] = [initialValue, invalidateType, invalidateWhenChanges];
  }

  const rerender = useRerender();
  const state = win.singletonCaches[name][0] as T;

  useEffect(() => {
    window.addEventListener(name, rerender);
    return () => window.removeEventListener(name, rerender);
  });
  return [state, (valueOrUpdateFunc: SetStateAction<T>) => dispatchUpdate(name, valueOrUpdateFunc)];
}

export function invalidateAll(invalidateType: InvalidateType, newKey?: string) {
  if (invalidateType === 'extensionTaxonomyGuid') {
    window.__extensionTaxonomyGuid = newKey!;
  }
  const win = window as WindowWithCaches;
  Object.entries(win.singletonCaches || {}).forEach(([name, cache]) => {
    if (cache[1] === invalidateType) {
      delete win.singletonCaches![name];
      window.dispatchEvent(new CustomEvent(name));
    }
  });
}

export function getSingletonState<T>(name: string): T | undefined {
  const win = window as WindowWithCaches;
  return win.singletonCaches?.[name]?.[0] as T | undefined;
}

export function dispatchUpdate<T>(name: string, valueOrUpdateFunc: SetStateAction<T>) {
  const win = window as WindowWithCaches;
  if (typeof valueOrUpdateFunc === 'function') {
    win.singletonCaches![name][0] = (valueOrUpdateFunc as (s: T) => T)(win.singletonCaches![name][0] as T);
  } else {
    win.singletonCaches![name][0] = valueOrUpdateFunc;
  }
  window.dispatchEvent(new CustomEvent(name));
}
